<template>
  <div :id="id" class="custom-presents">
    <div class="componata-container">
      <div class="grid-noGutter-equalHeight">
        <div class="col-5_md-12">
          <div>
            <FancyHeading class="custom-presents__heading">
              <template v-slot:title><span v-html="content.title"></span></template>
              <template v-slot:description>{{ content.description }}</template>
            </FancyHeading>

            <div class="custom-presents__cta">
              <div class="custom-presents__easy">
                <img src="@/assets/images/christmas-page/icon-easy.svg">
                <span>Einfach online <br><b>zusammenstellen</b></span>
              </div>

              <Button type="router" :to="`${content.cta_button.cta_link}`" size="large">{{ content.cta_button.cta_text }} <i class="icon icon-angle-right"></i></Button>
            </div>

            <Tabs
              ref="tabs"
              :data="navigationTabs"
              orientation="vertical"
              @click="ble()"
              @change="showTabContent($event)"
              class="custom-presents__tabs"
            />
          </div>
        </div>

        <div class="col-7_md-12">
          <div
            v-for="(item, index) in content.sections"
            :key="index"
            class="custom-presents__media"
            :class="{ '--is-visible' : activeContentIndex === index }"
          >
            <img
              :srcset="`
                ${item.media.url}?format=${webPFormat('png')}&width=1690&quality=80 1126w,
                ${item.media.url}?format=${webPFormat('png')}&width=845&quality=80 945w
              `"
              :src="`${item.media.url}?format=${webPFormat('png')}&width=845&quality=80`"
              :width="item.media.width / 2"
              :height="item.media.height / 2"
              class="custom-presents__pic"
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { helperMixin } from '@/mixins/HelperMixin';

import FancyHeading from '@/components/Pages/FancyHeading';
import Tabs from '@/components/Pages/Tabs';
import Button from '@/components/Common/Button';

export default {
  name: 'BusinessFeatures',
  mixins: [helperMixin],
  components: {
    FancyHeading,
    Tabs,
    Button
  },
  props: {
    id: {
      type: String,
      default: 'custom-presents'
    },
    content: {
      type: Object
    }
  },
  data () {
    return {
      navigationTabs: [],
      activeContentIndex: 0,
      autoplay: null
    };
  },
  methods: {
    showTabContent ($event) {
      this.activeContentIndex = $event;
      clearInterval(this.autoplay);
    },
  },
  created () {
    // Create navigation tabs object based on the content
    this.content.sections.forEach(item => {
      this.navigationTabs.push({
        icon: item.tab_icon,
        title: item.tab_title,
        description: item.tab_description
      });
    });
  },
  mounted () {
    this.autoplay = setInterval(() => {
      if (this.activeContentIndex === this.content.sections.length - 1) {
        this.activeContentIndex = -1;
      }

      this.activeContentIndex++;
      this.$refs.tabs.activeIndex = this.activeContentIndex;
    }, 5000);
  }
};
</script>

<style lang="scss" scoped>
/**
 * Custom Presents
 *
 * @author juanpablob <m.juanpablob@gmail.com>
 * @desc Content section for showcasing "create your own present" and Composer's features
 * @since 2022-07-04
**/

$component: 'custom-presents';

.#{$component} {
  overflow: hidden;
  background: linear-gradient(87.5deg, #FFF2FA 29.14%, rgba(255, 228, 236, 0.48) 88.55%), #FFFAE2;

  &__heading {
    --from: #472D39;
    --to: #EE4B86;
    --shadow: #ffffff01;

    @include margin-top(map-get($spacers, 6) * 2);
    @include margin-bottom(map-get($spacers, 4));
  }

  &__tabs {
    @include margin-bottom(map-get($spacers, 6) * 2);
    --color-active: #EE4B86;
  }

  &__cta {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    @include margin-top(map-get($spacers, 5));
    @include margin-bottom(map-get($spacers, 5) * 2);
  }

  &__easy {
    display: flex;
    flex-shrink: 1;
    align-items: flex-end;
    @include margin-bottom(map-get($spacers, 3));
    line-height: 120%;

    > img {
      @include rfs(2rem, width);
    }

    > span {
      @include padding-left(map-get($spacers, 2));
      color: $ink-lighter;
    }
  }

  &__media {
    display: none;
    @include padding-top(map-get($spacers, 6));

    &.--is-visible {
      display: flex;
      align-items: flex-end;
      animation: fade-in 0.6s ease-in-out forwards;
      will-change: transform, opacity;

      @keyframes fade-in {
        from {
          opacity: 0;
          transform: translateY(0.5rem);
        }

        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
    }
  }

  &__pic {
    @include imageRendering;
    width: auto;
    max-width: unset !important;
    pointer-events: none;
    user-select: none;
  }

  /** RESPONSIVENESS **/

  // ——————————————————————————————
  // Max LG
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-lg')) {
  }

  // ——————————————————————————————
  // Max MD
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-md')) {
    &__heading {
      width: 70%;
      @include margin(map-get($spacers, 6) * 2 auto map-get($spacers, 4) auto);
      text-align: center;
    }

    &__cta {
      justify-content: center;
    }

    &__tabs {
      margin-bottom: 0;
    }

    &__media {
      padding-top: 0;
    }

    &__pic {
      display: block;
      margin: 0 auto;
    }
  }

  // ——————————————————————————————
  // Max SM
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-sm')) {
    &__heading {
      width: 80%;
    }

    &__pic {
      width: 100%;
      height: auto;
    }

    &__cta {
      flex: 1 1 100%;
      // flex-direction: column-reverse;
      align-items: center;
    }

    &__easy {
      @include margin-bottom(map-get($spacers, 2));
    }
  }

  // ——————————————————————————————
  // Max XS
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-xs')) {
    &__heading {
      width: 90%;
    }
  }
}
</style>
